import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { Deployment } from "@/features/deployment-selection/model/deployment";

interface DeploymentCardProps {
  deployment: Deployment;
  onSelect: (deploymentId: string) => void;
}

export function DeploymentCard({ deployment, onSelect }: DeploymentCardProps) {
  const active = deployment.deployment_status.toLowerCase() === "active";

  return (
    <Card
      sx={(theme) => ({
        height: "100%",
        bgcolor: alpha(theme.palette.background.paper, active ? 0.86 : 0.62),
        opacity: active ? 1 : 0.78,
        transition: theme.transitions.create(["border-color", "transform", "background-color"]),
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.45),
          transform: "translateY(-2px)",
        },
      })}
    >
      <CardActionArea onClick={() => onSelect(deployment.deployment_id)} sx={{ height: "100%" }}>
        <CardContent sx={{ height: "100%", p: 3 }}>
          <Stack spacing={3} sx={{ height: "100%" }}>
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800 }}
                  noWrap
                  title={deployment.deployment_name}
                >
                  {deployment.deployment_name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={deployment.deployment_slug}
                >
                  {deployment.deployment_slug}
                </Typography>
              </Box>
              <Chip
                label={deployment.deployment_status}
                size="small"
                color={active ? "success" : "warning"}
                variant="outlined"
                sx={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}
              />
            </Stack>

            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                bgcolor: alpha(theme.palette.common.black, 0.08),
              })}
            >
              <Metric label="Region" value={deployment.region ?? "N/A"} />
              <Metric label="Customer" value={deployment.customer_slug} />
              <Metric label="Time Zone" value={deployment.time_zone ?? "N/A"} />
            </Box>

            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center", gap: 2, mt: "auto" }}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                Roles:{" "}
                {deployment.access.role_keys.length > 0
                  ? deployment.access.role_keys.join(", ")
                  : "none"}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ alignItems: "center", color: "primary.main", flexShrink: 0 }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase" }}
                >
                  Open dashboard
                </Typography>
                <ArrowForwardIosIcon sx={{ fontSize: 11 }} />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

interface MetricProps {
  label: string;
  value: string;
}

function Metric({ label, value }: MetricProps) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 9, textTransform: "uppercase" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" noWrap title={value} sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
    </Stack>
  );
}
